import { useState } from "react";
import { useLocation } from "react-router";
import { useAtomValue } from "jotai";
import { MessageSquareDot, Check } from "lucide-react";
import { localeAtom } from "../../stores/locale";
import { selectedDomainAtom } from "../../stores/observatory";
import { useT } from "../../lib/i18n";
import NewTicketModal from "./NewTicketModal";

// A floating "feedback on this view" affordance present on every galaxy page.
// It captures the view context structurally — page / galaxy / member / domain —
// from the URL (source of truth) + the focused-domain atom, so feedback arrives
// already knowing WHERE the user was. Screenshots are intentionally out of
// scope (structured context only). The context is shown in the modal before
// sending; nothing is collected silently.
export default function ContextFeedbackButton() {
  const location = useLocation();
  const locale = useAtomValue(localeAtom);
  const domain = useAtomValue(selectedDomainAtom);
  const t = useT(locale);
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);

  const segments = location.pathname.split("/").filter(Boolean);
  const account = segments[0] || undefined;
  const after = segments.slice(1);
  const page = after[0] || "observatory";
  const member = after[0] === "member" ? after[1] : undefined;

  // Don't offer feedback-on-this-view from the feedback surfaces themselves.
  if (page === "feedback" || page === "admin") return null;

  const context: Record<string, unknown> = {
    page,
    path: location.pathname,
    galaxy: account,
    member,
    domain: domain || undefined,
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setSent(false);
          setOpen(true);
        }}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-md rounded-full px-lg py-md text-sm transition-all"
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          color: "var(--text-muted)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}
        title={t("feedback.contextButton")}
      >
        {sent ? (
          <>
            <Check size={15} style={{ color: "var(--gb-green)" }} />
            <span style={{ color: "var(--gb-green)" }}>{t("feedback.sentShort")}</span>
          </>
        ) : (
          <>
            <MessageSquareDot size={15} />
            <span className="hidden sm:inline">{t("feedback.contextButton")}</span>
          </>
        )}
      </button>

      {open && (
        <NewTicketModal
          accountLogin={account}
          context={context}
          onClose={() => setOpen(false)}
          onCreated={() => {
            setOpen(false);
            setSent(true);
            setTimeout(() => setSent(false), 4000);
          }}
        />
      )}
    </>
  );
}
